import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

const KEY = "companies";

export function useCompanyList(options?: {
	status?: string | undefined;
	search?: string | undefined;
	createdAtFrom?: string | undefined;
	createdAtTo?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.status) params.set("status", options.status);
			if (options?.search) params.set("search", options.search);
			if (options?.createdAtFrom) params.set("createdAtFrom", options.createdAtFrom);
			if (options?.createdAtTo) params.set("createdAtTo", options.createdAtTo);
			return api.get<{ items: Company[]; total: number }>(`/api/companies?${params.toString()}`);
		},
	});
}

export function useCompanyDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () => api.get<Company>(`/api/companies/${id}`),
		enabled: !!id,
	});
}

export function useCreateCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateCompanyInput) => api.post<Company>("/api/companies", data),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useUpdateCompany() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: string;
			data: Partial<Omit<Company, "address">> & { address?: CompanyAddressInput };
		}) => api.patch<Company>(`/api/companies/${id}`, data),
		onSuccess: (_, vars) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useSapCurrencies() {
	return useQuery({
		queryKey: ["sap", "currencies"],
		queryFn: () => api.get<{ code: string; name: string }[]>("/api/sap/currencies"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSapStates(countryCode?: string) {
	return useQuery({
		queryKey: ["sap", "states", countryCode ?? "TR"],
		queryFn: () =>
			api.get<{ code: string; name: string }[]>(
				`/api/sap/states?country=${encodeURIComponent(countryCode ?? "TR")}`,
			),
		staleTime: 60 * 60 * 1000,
	});
}

export function useSapCountries() {
	return useQuery({
		queryKey: ["sap", "countries"],
		queryFn: () => api.get<{ code: string; name: string }[]>("/api/sap/countries"),
		staleTime: 60 * 60 * 1000,
	});
}

export function useSapBpGroups() {
	return useQuery({
		queryKey: ["sap", "bp-groups"],
		queryFn: () => api.get<{ code: number; name: string }[]>("/api/sap/bp-groups"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSapBpSeries() {
	return useQuery({
		queryKey: ["sap", "bp-series"],
		queryFn: () =>
			api.get<{ series: number; name: string; nextCode: string | null; isManual: boolean }[]>(
				"/api/sap/bp-series",
			),
		staleTime: 5 * 60 * 1000,
	});
}

export function useSalesPersons() {
	return useQuery({
		queryKey: ["sap", "salespersons"],
		queryFn: () =>
			api.get<{ id: string; sapSlpCode: number; name: string }[]>("/api/sap/salespersons"),
		staleTime: 5 * 60 * 1000,
	});
}

export function useAssignableUsers(options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: [KEY, "assignable-users"],
		queryFn: () => api.get<{ id: string; name: string }[]>("/api/companies/assignable-users"),
		staleTime: 5 * 60 * 1000,
		enabled: options?.enabled !== false,
	});
}

export interface CompanyAddressInput {
	street?: string | undefined;
	city?: string | undefined;
	state?: string | undefined;
	postalCode?: string | undefined;
	country?: string | undefined;
}

export interface CreateCompanyInput {
	legalName: string;
	tradeName?: string | undefined;
	status: "lead" | "customer" | "vendor" | "inactive";
	sapSeries?: number | undefined;
	sapGroupCode?: number | undefined;
	currency?: string | undefined;
	currencies?: string[] | undefined;
	taxNumber?: string | undefined;
	taxOffice?: string | undefined;
	email?: string | undefined;
	phone?: string | undefined;
	fax?: string | undefined;
	website?: string | undefined;
	industry?: string | undefined;
	notes?: string | undefined;
	ownerId?: string | undefined;
	address?: CompanyAddressInput | undefined;
}

interface CompanyAddress {
	id: string;
	street: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string | null;
}

interface Owner {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

export interface Company {
	id: string;
	owner: Owner | null;
	legalName: string;
	address?: CompanyAddress | null;
	tradeName: string | null;
	taxNumber: string | null;
	taxOffice: string | null;
	registrationNumber: string | null;
	industry: string | null;
	size: string | null;
	website: string | null;
	phone: string | null;
	fax: string | null;
	balance: string | null;
	currency: string | null;
	notes: string | null;
	status: "lead" | "customer" | "vendor" | "inactive";
	sapCardCode: string | null;
	sapGroupCode: number | null;
	sapCurrency: string | null;
	sapLicTradNum: string | null;
	sapBalance: string | null;
	sapCreditLimit: string | null;
	lastSyncedAt: string | null;
	syncStatus: string | null;
	ownerId: string | null;
	teamId: string | null;
	visibility: string;
	createdAt: string;
	updatedAt: string;
}
